import type { BookingRecord, CheckoutDraft, ProductType } from '../types'

const PREFIX = 'ohmytrip:v1:'

const read = <T>(key: string, fallback: T): T => {
  try {
    const value = localStorage.getItem(PREFIX + key)
    return value ? JSON.parse(value) as T : fallback
  } catch {
    return fallback
  }
}

const write = <T>(key: string, value: T) => localStorage.setItem(PREFIX + key, JSON.stringify(value))

export const bookingRepository = {
  saveDraft(draft: CheckoutDraft) { write('checkoutDraft', draft) },
  getDraft() { return read<CheckoutDraft | null>('checkoutDraft', null) },
  clearDraft() { localStorage.removeItem(PREFIX + 'checkoutDraft') },
  saveBooking(booking: BookingRecord) {
    const items = read<BookingRecord[]>('bookings', [])
    write('bookings', [booking, ...items.filter((item) => item.id !== booking.id)])
  },
  listBookings(userKey?: string) {
    const items = read<BookingRecord[]>('bookings', [])
    return userKey ? items.filter((item) => item.userKey === userKey) : items
  },
  getBooking(id: string) { return read<BookingRecord[]>('bookings', []).find((item) => item.id === id) },
  findGuestBooking(id: string, email: string) {
    return read<BookingRecord[]>('bookings', []).find((item) => item.id.toLowerCase() === id.toLowerCase() && item.guest.email.toLowerCase() === email.toLowerCase())
  },
  cancelBooking(id: string) {
    const items = read<BookingRecord[]>('bookings', []).map((item) => item.id === id ? { ...item, status: 'cancelled' as const } : item)
    write('bookings', items)
  },
}

export const preferenceRepository = {
  getWishlist() { return read<string[]>('wishlist', []) },
  toggleWishlist(id: string) {
    const current = read<string[]>('wishlist', [])
    const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    write('wishlist', next)
    return next
  },
  addRecentSearch(type: ProductType, query: string) {
    const current = read<Array<{ type: ProductType; query: string; at: string }>>('recentSearches', [])
    write('recentSearches', [{ type, query, at: new Date().toISOString() }, ...current.filter((item) => !(item.type === type && item.query === query))].slice(0, 6))
  },
  getRecentSearches() { return read<Array<{ type: ProductType; query: string; at: string }>>('recentSearches', []) },
}

export const createBookingId = () => `OMT-${new Date().toISOString().slice(2, 10).replaceAll('-', '')}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`
